/**
 * Invoice List Component
 *
 * Displays a list of invoices with filtering, sorting, and actions.
 * Filter state is owned by the parent (useInvoicesPage) and passed as serverFilters.
 */

import { useEffect } from 'react'

import { PageHeaderWithBreadcrumbs } from '@elsapiens/layout'
import { usePageHeader, useShortcuts } from '@elsapiens/providers'
import { Button } from '@elsapiens/ui'
import { Link, useNavigate } from 'react-router-dom'

import {
  buildEmptyStateDescription,
  buildListTitle,
} from './invoice-list/invoiceListHelpers'
import {
  InvoiceCardBody,
  InvoiceSummaryBar,
} from './invoice-list/InvoiceListSubcomponents'
import { buildInvoiceColumns } from './invoice-list/InvoiceTableColumns'
import { useBaseCurrency } from '../../hooks/use-currencies'
import { DraftPostedToggle } from '../../pages/journal-entries/JournalEntriesPage'

import type { Invoice } from '../../types/billing'
import type { FilterBarCondition } from '@elsapiens/ui'

export interface ServerFilterState {
  invoices: Invoice[]
  searchQuery: string
  setSearchQuery: (q: string) => void
  sortField: string
  setSortField: (f: string) => void
  sortDirection: 'asc' | 'desc'
  setSortDirection: (d: 'asc' | 'desc') => void
  activeTab: string
  setActiveTab: (t: string) => void
  conditions: FilterBarCondition[]
  setConditions: (c: FilterBarCondition[]) => void
  hasFilters: boolean
  page: number
  setPage: (p: number) => void
  totalPages: number
  totalCount: number
  sumTotalAmount: number
  sumTaxAmount: number
  currencyOptions: { value: string; label: string }[]
}

export interface InvoiceListProps {
  serverFilters: ServerFilterState
  /** Whether the list is loading */
  isLoading?: boolean
  /** Whether more items are being loaded */
  isLoadingMore?: boolean
  /** Callback when an invoice is selected for viewing */
  onView: (invoice: Invoice) => void
  /** Callback when an invoice is selected for editing */
  onEdit: (invoice: Invoice) => void
  /** Callback to generate PDF for an invoice */
  onGeneratePDF: (invoice: Invoice) => void
  /** Callback to post an invoice */
  onPost?: (invoice: Invoice) => void
  /** Callback to void an invoice */
  onVoid?: (invoice: Invoice) => void
  /** Callback to create a new invoice */
  onCreate: () => void
  /** Whether showing proforma (draft) or final (posted) invoices */
  isProforma?: boolean
  /** Whether showing vendor bills (AP) */
  isVendor?: boolean
  /** Custom empty state node (from parent page) */
  emptyStateNode?: React.ReactNode
  /** Custom collapsible info node (from parent page) */
  collapsibleInfoNode?: React.ReactNode
}

interface ShortcutsParams {
  entityLabel: string
  draftPath: string
  postedPath: string
  onCreate: () => void
  register: ReturnType<typeof useShortcuts>['register']
  navigate: ReturnType<typeof useNavigate>
}

function useInvoiceShortcuts(params: ShortcutsParams): void {
  const { entityLabel, draftPath, postedPath, onCreate, register, navigate } = params
  const { pushContext, popContext } = useShortcuts()
  useEffect(() => {
    pushContext('invoices')
    return () => popContext('invoices')
  }, [pushContext, popContext])
  useEffect(() => {
    const labelLower = entityLabel.toLowerCase()
    const category = `${entityLabel}s`
    const cleanups = [
      register({ id: `${labelLower}-new`, keys: 'n', label: `New ${entityLabel}`, description: `Create a new ${labelLower}`, category, scope: 'context', context: 'invoices', action: onCreate }),
      register({ id: `${labelLower}-drafts`, keys: 'mod+k', label: 'View Drafts', description: `Switch to draft ${labelLower}s`, category, scope: 'context', context: 'invoices', action: () => navigate(draftPath) }),
      register({ id: `${labelLower}-posted`, keys: 'ctrl+l', label: 'View Posted', description: `Switch to posted ${labelLower}s`, category, scope: 'context', context: 'invoices', action: () => navigate(postedPath) }),
    ]
    return () => cleanups.forEach(fn => fn())
  }, [register, navigate, onCreate, draftPath, postedPath, entityLabel])
}

interface ListHeaderProps {
  isProforma: boolean
  isVendor: boolean
  listTitle: string
  draftPath: string
  postedPath: string
  onCreate: () => void
}

function InvoiceListHeader(p: ListHeaderProps): JSX.Element {
  const navigate = useNavigate()
  return (
    <PageHeaderWithBreadcrumbs
      description={p.isVendor ? 'Manage vendor bills and purchase invoices' : 'Manage and track your invoices'}
      linkComponent={Link}
      title={p.listTitle}
      variant="card"
    >
      <DraftPostedToggle
        activeTab={p.isProforma ? 'drafts' : 'posted'}
        onSwitch={(tab) => navigate(tab === 'drafts' ? p.draftPath : p.postedPath)}
      />
      <Button variant="primary" onClick={p.onCreate}>
        + {p.isVendor ? 'New Vendor Bill' : 'New Invoice'}
      </Button>
    </PageHeaderWithBreadcrumbs>
  )
}

function useInvoiceListPageHeader(isVendor: boolean, listTitle: string): void {
  usePageHeader({
    title: listTitle,
    description: isVendor ? 'Manage vendor bills and purchase invoices' : 'Manage and track your invoices',
    breadcrumbs: [
      { label: 'Home', href: '/', as: Link },
      isVendor
        ? { label: 'Purchases', href: '/purchases', as: Link }
        : { label: 'Sales', href: '/sales', as: Link },
      { label: listTitle },
    ],
  })
}

interface ListBodyProps {
  serverFilters: ServerFilterState
  isLoading: boolean
  isLoadingMore: boolean
  isVendor: boolean
  baseCurrency: string
  columns: ReturnType<typeof buildInvoiceColumns>
  partyLabel: string
  emptyStateHeading: string
  emptyStateDescription: string
  emptyStateButtonLabel: string
  emptyStateNode?: React.ReactNode
  collapsibleInfoNode?: React.ReactNode
  onView: (invoice: Invoice) => void
  onCreate: () => void
}

function InvoiceListBody(p: ListBodyProps): JSX.Element {
  const { serverFilters: sf } = p
  const handleClearFilters = (): void => {
    sf.setSearchQuery('')
    sf.setConditions([])
    sf.setActiveTab('all')
  }
  return (
    <div className="el-p-lg">
      <InvoiceSummaryBar
        baseCurrency={p.baseCurrency}
        count={sf.totalCount}
        totalAmount={sf.sumTotalAmount}
        totalTax={sf.sumTaxAmount}
      />
      <InvoiceCardBody
        activeTab={sf.activeTab}
        columns={p.columns}
        conditions={sf.conditions}
        currencyOptions={sf.currencyOptions}
        emptyStateButtonLabel={p.emptyStateButtonLabel}
        emptyStateDescription={p.emptyStateDescription}
        emptyStateHeading={p.emptyStateHeading}
        emptyStateNode={p.emptyStateNode}
        filteredInvoices={sf.invoices}
        hasFilters={sf.hasFilters}
        isLoading={p.isLoading}
        isLoadingMore={p.isLoadingMore}
        isVendor={p.isVendor}
        page={sf.page}
        partyLabel={p.partyLabel}
        searchQuery={sf.searchQuery}
        sortDirection={sf.sortDirection}
        sortField={sf.sortField}
        totalCount={sf.totalCount}
        totalPages={sf.totalPages}
        onClearFilters={handleClearFilters}
        onConditionsChange={sf.setConditions}
        onCreate={p.onCreate}
        onPageChange={sf.setPage}
        onSearchChange={sf.setSearchQuery}
        onSortChange={(id, dir) => { sf.setSortField(id); sf.setSortDirection(dir) }}
        onTabChange={sf.setActiveTab}
        onView={p.onView}
      />
      {!p.isLoading && sf.invoices.length > 0 && p.collapsibleInfoNode}
    </div>
  )
}

export function InvoiceList(props: InvoiceListProps): JSX.Element {
  const {
    serverFilters, isLoading = false, isLoadingMore = false,
    onView, onEdit, onGeneratePDF, onPost, onVoid, onCreate,
    isProforma = true, isVendor = false, emptyStateNode, collapsibleInfoNode,
  } = props
  const navigate = useNavigate()
  const { register } = useShortcuts()
  const baseCurrency = useBaseCurrency()
  const partyLabel = isVendor ? 'Supplier' : 'Customer'
  const listTitle = buildListTitle(isVendor, isProforma)
  const draftPath = isVendor ? '/bills/draft' : '/invoices/proforma'
  const postedPath = isVendor ? '/bills/final' : '/invoices/final'
  const entityLabel = isVendor ? 'Bill' : 'Invoice'

  useInvoiceShortcuts({ entityLabel, draftPath, postedPath, onCreate, register, navigate })
  useInvoiceListPageHeader(isVendor, listTitle)

  const columns = buildInvoiceColumns({ partyLabel, baseCurrency, onView, onEdit, onGeneratePDF, onPost, onVoid })

  return (
    <div className="flex flex-col min-w-0">
      <InvoiceListHeader
        draftPath={draftPath}
        isProforma={isProforma}
        isVendor={isVendor}
        listTitle={listTitle}
        postedPath={postedPath}
        onCreate={onCreate}
      />
      <InvoiceListBody
        baseCurrency={baseCurrency}
        collapsibleInfoNode={collapsibleInfoNode}
        columns={columns}
        emptyStateButtonLabel={isVendor ? 'Create your first vendor bill' : 'Create your first invoice'}
        emptyStateDescription={buildEmptyStateDescription(serverFilters.hasFilters, isVendor)}
        emptyStateHeading={isVendor ? 'No vendor bills found' : 'No invoices found'}
        emptyStateNode={emptyStateNode}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        isVendor={isVendor}
        partyLabel={partyLabel}
        serverFilters={serverFilters}
        onCreate={onCreate}
        onView={onView}
      />
    </div>
  )
}

export default InvoiceList
